import {
  Autocomplete,
  AutocompleteProps,
  ChipTypeMap,
  TextField,
  TextFieldProps,
} from '@mui/material';
import { GROUP_AUTOCOMPLETE } from 'constants/redux';
import { useAppDispatch, useAppSelector } from 'core/redux';
import UserGroups, { FlatUserGroup } from 'core/redux/reducers/userGroups';
import { fetchUserGroups } from 'core/redux/thunks';
import React, { useEffect } from 'react';

interface Props<
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
> {
  AutocompleteProps: Omit<
    AutocompleteProps<
      FlatUserGroup,
      Multiple,
      DisableClearable,
      FreeSolo,
      ChipComponent
    >,
    'renderInput' | 'options'
  >;
  TextFieldProps?: TextFieldProps;
}

function UserGroupAutocomplete<M extends boolean | undefined>({
  AutocompleteProps,
  TextFieldProps = {},
}: Props<M>) {
  const dispatch = useAppDispatch();
  const { isReady } = useAppSelector((state) =>
    UserGroups.selectors.selectList(state, GROUP_AUTOCOMPLETE),
  );

  useEffect(() => {
    if (isReady) return;
    dispatch(fetchUserGroups({ page: 1 }, GROUP_AUTOCOMPLETE));
  }, [isReady, dispatch]);

  const options = useAppSelector(
    (state) =>
      UserGroups.selectors.selectEntitiesFromList(
        state,
        GROUP_AUTOCOMPLETE,
      ) as FlatUserGroup[],
  );
  return (
    <Autocomplete
      id="group"
      {...AutocompleteProps}
      loading={!isReady}
      options={options}
      getOptionLabel={(option) => option.name || option.id.toString()}
      renderInput={(params) => (
        <TextField
          {...params}
          {...TextFieldProps}
          label="User Group"
          margin="none"
        />
      )}
    />
  );
}

export default UserGroupAutocomplete;
